import React, { useState } from 'react';
import { Form, Button, Card, InputGroup, Spinner, Row, Col } from 'react-bootstrap';
import { FaUser, FaLock, FaEye } from 'react-icons/fa';
import { Formik, Form as FForm } from 'formik';
import { string, object, InferType } from 'yup'; // for only what you need
import { useHistory } from 'react-router-dom';
import { FormCol3, FormCol6, GridLinkButton } from 'motion-components';
import { FieldSelectMultitenantAliases } from './fs-multitenant-aliases';
import { useTranslation } from 'react-i18next';
import './login-form.scss';
import { isCombatt, isFotokiklosi, isRebatteryLiion } from 'shared-pages/common/constants';
import { ChristmasTree } from 'shared-pages/components/decorative/christmas-tree';

/**
 * Setup Yup Validation
 */
const validationSchema = object().shape({
  name: string().required(),
  password: string().required(),
});

export type FormDataType = InferType<typeof validationSchema>;

type Props = {
  message?: string | null;
  loading?: boolean;
  onSubmit: (values, action) => void;
  showAliases?: boolean;
};

export const LoginForm = ({
  message = '',
  loading = false,
  onSubmit,
  showAliases = false,
}: Props) => {
  /*-------------------- Body -----------------------------------------------*/
  const [show, setShow] = useState(false);
  const history = useHistory();
  const { t, i18n } = useTranslation();

  /*-------------------- Render ---------------------------------------------*/

  return (
    <Formik
      initialValues={{ name: '', password: '' }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formikBag) => (
        <div className="login-form-wrapper">
          <ChristmasTree />
          <Card className="bg-light shadow-sm p-3 mb-3 login-frame">
            <Card.Body>
              <div className="d-flex flex-row justify-content-end">
                <div
                  style={{ color: 'black', opacity: i18n.language === 'en' ? 1 : 0.6 }}
                  onClick={() => {
                    i18n.changeLanguage('en');
                  }}
                >
                  {'EN'}
                  <img
                    alt=""
                    src={process.env.PUBLIC_URL + '/ukFlagEmoji.png'}
                    className="d-inline-block align-top mx-1 login-lang-flag"
                    style={{ marginTop: '-3px' }}
                  />
                </div>
                <span className="mx-3"></span>
                <div
                  style={{ color: 'black', opacity: i18n.language !== 'en' ? 1 : 0.6 }}
                  onClick={() => {
                    i18n.changeLanguage('gr');
                  }}
                >
                  {'ΕΛ'}
                  <img
                    alt=""
                    src={process.env.PUBLIC_URL + '/greekFlagEmoji.png'}
                    className="d-inline-block align-top mx-1 login-lang-flag"
                    style={{ marginTop: '-3px' }}
                  />
                </div>
              </div>
              <Card.Title className="text-center mb-4 mt-1" as="h4">
                {isFotokiklosi && (
                  <h1
                    style={{ color: '#006EB6' }}
                    className="mt-3 mb-2 d-flex align-items-center justify-content-center text-center login-brand-title"
                  >
                    <img
                      alt=""
                      src={process.env.PUBLIC_URL + '/logo.png'}
                      className="login-brand-logo"
                    />
                    <span style={{fontSize: '3rem', paddingLeft: '0.2rem'}}>Φωτοκύκλωση α.ε.</span>
                  </h1>
                )}
                {!isFotokiklosi && (
                  <img
                    alt=""
                    src={process.env.PUBLIC_URL + '/logo.png'}
                    className="mx-auto d-block login-logo"
                  />
                )}
              </Card.Title>
              <p className="text-danger text-center">{message && ''}</p>

              <Form as={FForm}>
                <Form.Group className="mb-3">
                  <InputGroup>
                    <InputGroup.Text style={{ maxHeight: '32px' }}>
                      <FaUser />
                    </InputGroup.Text>
                    <Form.Control
                      style={{ height: '32px' }}
                      size="lg"
                      id="name"
                      type="text"
                      autoComplete="username"
                      placeholder={t('Χρήστης')}
                      {...formikBag.getFieldProps('name')}
                      isInvalid={!!formikBag.touched.name && !!formikBag.errors.name}
                    />
                    {!!formikBag.touched.name && !!formikBag.errors.name && (
                      <Form.Control.Feedback type="invalid">
                        {t('Δηλώστε όνομα για την είσοδο σας.')}
                      </Form.Control.Feedback>
                    )}
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3 ">
                  <InputGroup>
                    <InputGroup.Text style={{ maxHeight: '32px' }}>
                      <FaLock />
                    </InputGroup.Text>
                    <Form.Control
                      style={{ height: '32px' }}
                      size="lg"
                      id="password"
                      type={show ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder={t('Κωδικός Πρόσβασης')}
                      {...formikBag.getFieldProps('password')}
                      isInvalid={!!formikBag.touched.password && !!formikBag.errors.password}
                    />
                    <Button
                      onClick={() => setShow((value) => !value)}
                      className="login-password-toggle"
                      variant="secondary"
                      type="button"
                      aria-label={show ? t('Απόκρυψη κωδικού') : t('Εμφάνιση κωδικού')}
                    >
                      <FaEye />
                    </Button>
                    {!!formikBag.touched.password && !!formikBag.errors.password && (
                      <Form.Control.Feedback type="invalid">
                        {t('Δηλώστε κωδικό για την είσοδο σας.')}
                      </Form.Control.Feedback>
                    )}
                  </InputGroup>
                </Form.Group>

                {showAliases && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <FieldSelectMultitenantAliases label={t('Επιλέξτε Εγκατάσταση')} />
                  </div>
                )}

                <div className="d-grid">
                  <Button
                    variant={isCombatt ? 'success' : 'primary'}
                    type="submit"
                    disabled={!(formikBag.isValid && formikBag.dirty)}
                    size="lg"
                    style={{ paddingTop: '2px', height: '36px' }}
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" className="mx-2" />
                    ) : (
                      <span></span>
                    )}
                    <span>{t('Είσοδος')}</span>
                  </Button>
                </div>
              </Form>
            </Card.Body>
            {/* <a onClick={() => history.push(generatePath(getRouteByName('ForgotPw')))} className="forgot-pw" href="#">
            <p> Ξέχασα τον κωδικό μου </p>{' '}
          </a> */}
            {/* <GridLinkButton
            onClick={() => {
              history.push('/forgotpw');
            }}
          >
            Ξέχασα τον κωδικό μου !
          </GridLinkButton> */}
          </Card>
        </div>
      )}
    </Formik>
  );
};
